import { FormEvent, useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { CollapsibleFormSection } from '../../components/ui/CollapsibleFormSection';
import { DeleteIconButton } from '../../components/ui/DeleteIconButton';
import { EditIconButton } from '../../components/ui/EditIconButton';
import { FormActionBar } from '../../components/ui/FormActionBar';
import { SortOrderControls } from '../../components/ui/SortOrderControls';
import { useConfirmDialog } from '../../contexts/ConfirmDialogContext';
import { useCollapsibleItemForm } from '../../hooks/useCollapsibleItemForm';
import { useSortOrderReorder } from '../../hooks/useSortOrderReorder';
import { JOB_LEVEL_OPTIONS } from '../../services/collaboratorProfileOptions';
import {
  JobPositionAttributionKind,
  listJobAttributionKinds,
} from '../../services/jobAttributionKindsApi';
import {
  JobPositionAttribution,
  JobPositionAttributionUpsertPayload,
  createJobPositionAttribution,
  deleteJobPositionAttribution,
  listJobPositionAttributions,
  updateJobPositionAttribution,
} from '../../services/jobPositionAttributionsApi';
import { nextSortOrder } from '../../utils/sortOrderList';

type JobPositionAttributionsPanelProps = {
  jobPositionId: string;
  canWrite: boolean;
};

type AttributionFormState = Omit<JobPositionAttributionUpsertPayload, 'sortOrder'>;

const ATTRIBUTION_DESCRIPTION_MAX_LENGTH = 4000;

const emptyForm: AttributionFormState = {
  jobLevel: 'Junior',
  kindId: '',
  title: '',
  description: '',
  isActive: true,
};

function buildAttributionPayload(
  item: JobPositionAttribution,
  sortOrder: number,
): JobPositionAttributionUpsertPayload {
  return {
    jobLevel: item.jobLevel,
    kindId: item.kindId,
    title: item.title,
    description: item.description,
    sortOrder,
    isActive: item.isActive,
  };
}

export function JobPositionAttributionsPanel({
  jobPositionId,
  canWrite,
}: JobPositionAttributionsPanelProps) {
  const { confirm } = useConfirmDialog();
  const { formExpanded, expandForm, collapseForm, handleCreateSuccess, toggleOrCancel } =
    useCollapsibleItemForm();
  const [kinds, setKinds] = useState<JobPositionAttributionKind[]>([]);
  const [items, setItems] = useState<JobPositionAttribution[]>([]);
  const [levelFilter, setLevelFilter] = useState('');
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<AttributionFormState>(emptyForm);
  const [saving, setSaving] = useState(false);

  const filteredItems = useMemo(
    () => (levelFilter ? items.filter((item) => item.jobLevel === levelFilter) : items),
    [items, levelFilter],
  );

  async function loadData(): Promise<void> {
    setLoading(true);
    try {
      const [kindList, attributionList] = await Promise.all([
        listJobAttributionKinds(true),
        listJobPositionAttributions(jobPositionId),
      ]);
      setKinds(kindList);
      setItems(attributionList);
      setForm((current) =>
        current.kindId || kindList.length === 0
          ? current
          : { ...current, kindId: kindList[0].id },
      );
    } catch {
      toast.error('Falha ao carregar atribuições do cargo.');
    } finally {
      setLoading(false);
    }
  }

  const { sortedItems, moving, move } = useSortOrderReorder({
    items: filteredItems,
    canReorder: canWrite,
    updateItem: async (item, sortOrder) => {
      await updateJobPositionAttribution(jobPositionId, item.id, buildAttributionPayload(item, sortOrder));
    },
    onReordered: () => loadData(),
  });

  useEffect(() => {
    void loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPositionId]);

  function resetForm(): void {
    setEditingId(null);
    setForm({
      ...emptyForm,
      kindId: kinds[0]?.id ?? '',
    });
  }

  function startEdit(item: JobPositionAttribution): void {
    expandForm();
    setEditingId(item.id);
    setForm({
      jobLevel: item.jobLevel,
      kindId: item.kindId,
      title: item.title,
      description: item.description,
      isActive: item.isActive,
    });
  }

  async function handleSubmit(event: FormEvent): Promise<void> {
    event.preventDefault();
    if (!canWrite) return;

    const editingItem = editingId ? items.find((item) => item.id === editingId) : null;
    const peersForOrder = items.filter((item) => item.jobLevel === form.jobLevel);
    const payload: JobPositionAttributionUpsertPayload = {
      ...form,
      sortOrder: editingItem?.sortOrder ?? nextSortOrder(peersForOrder),
    };

    setSaving(true);
    try {
      if (editingId) {
        await updateJobPositionAttribution(jobPositionId, editingId, payload);
        toast.success('Atribuição atualizada.');
        collapseForm(resetForm);
      } else {
        await createJobPositionAttribution(jobPositionId, payload);
        toast.success('Atribuição criada.');
        handleCreateSuccess(resetForm);
      }
      await loadData();
    } catch {
      toast.error('Falha ao salvar atribuição.');
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete(item: JobPositionAttribution): Promise<void> {
    const ok = await confirm({
      title: 'Excluir atribuição',
      message: `Excluir "${item.title}"?`,
      confirmLabel: 'Excluir',
      tone: 'danger',
    });
    if (!ok) return;

    try {
      await deleteJobPositionAttribution(jobPositionId, item.id);
      toast.success('Atribuição excluída.');
      if (editingId === item.id) {
        collapseForm(resetForm);
      }
      await loadData();
    } catch {
      toast.error('Falha ao excluir atribuição.');
    }
  }

  const levelLabel = (value: string) =>
    JOB_LEVEL_OPTIONS.find((option) => option.value === value)?.label ?? value;

  const formTitle = editingId ? 'Editar atribuição' : 'Nova atribuição';
  const editingFormSummary = editingId
    ? `Editando ${form.title || 'atribuição selecionada'} — clique para expandir`
    : undefined;

  return (
    <section className="erp-card mt-4">
      <div className="erp-card-body space-y-4">
        <div>
          <h3 className="font-semibold text-secondary-900">Atribuições do cargo x nível</h3>
          <p className="text-sm text-secondary-500">
            Orientações do cargo para cada nível. Não são itens avaliáveis.
          </p>
        </div>

        {canWrite ? (
          <CollapsibleFormSection
            title={formTitle}
            expanded={formExpanded}
            onToggle={() => toggleOrCancel(resetForm)}
            editingSummary={editingFormSummary}
            createSummary="Clique para expandir e cadastrar uma nova atribuição"
            isEditing={!!editingId}
            className="mb-0"
          >
            <form onSubmit={(event) => void handleSubmit(event)} className="erp-form px-4 pb-4 pt-3 space-y-3">
              <div className="erp-form-grid md:grid-cols-2">
                <select
                  className="erp-input-compact"
                  value={form.jobLevel}
                  required
                  onChange={(event) => setForm({ ...form, jobLevel: event.target.value })}
                >
                  {JOB_LEVEL_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <select
                  className="erp-input-compact"
                  value={form.kindId}
                  required
                  onChange={(event) => setForm({ ...form, kindId: event.target.value })}
                >
                  <option value="">Tipo</option>
                  {kinds.map((kind) => (
                    <option key={kind.id} value={kind.id}>
                      {kind.name}
                    </option>
                  ))}
                </select>
                <input
                  className="erp-input-compact md:col-span-2"
                  placeholder="Título da atribuição"
                  value={form.title}
                  required
                  onChange={(event) => setForm({ ...form, title: event.target.value })}
                />
                <textarea
                  className="erp-input-compact md:col-span-2 min-h-[72px]"
                  placeholder="Descrição (opcional)"
                  value={form.description}
                  maxLength={ATTRIBUTION_DESCRIPTION_MAX_LENGTH}
                  onChange={(event) => setForm({ ...form, description: event.target.value })}
                />
                <span className="text-xs text-secondary-500 md:col-span-2">
                  {(form.description ?? '').length} / {ATTRIBUTION_DESCRIPTION_MAX_LENGTH} caracteres
                </span>
              </div>
              <FormActionBar
                onCancel={() => collapseForm(resetForm)}
                cancelLabel="Cancelar"
              >
                <button type="submit" className="erp-btn-primary" disabled={saving}>
                  {saving ? 'Salvando...' : editingId ? 'Atualizar' : 'Adicionar'}
                </button>
              </FormActionBar>
            </form>
          </CollapsibleFormSection>
        ) : null}

        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm font-medium text-secondary-800">Listagem</p>
          <select
            className="erp-input-compact max-w-xs"
            value={levelFilter}
            onChange={(event) => setLevelFilter(event.target.value)}
            aria-label="Filtrar por nível"
          >
            <option value="">Todos os níveis</option>
            {JOB_LEVEL_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        {loading ? (
          <p className="text-sm text-secondary-500">Carregando atribuições...</p>
        ) : sortedItems.length === 0 ? (
          <p className="text-sm text-secondary-500">Nenhuma atribuição cadastrada para este filtro.</p>
        ) : (
          <ul className="space-y-2">
            {sortedItems.map((item, index) => (
              <li key={item.id} className="rounded-lg border border-secondary-200 px-3 py-2">
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-start gap-2">
                    {canWrite ? (
                      <SortOrderControls
                        canMoveUp={index > 0}
                        canMoveDown={index < sortedItems.length - 1}
                        disabled={moving}
                        onMoveUp={() => void move(index, 'up')}
                        onMoveDown={() => void move(index, 'down')}
                      />
                    ) : null}
                    <div>
                      <div className="text-xs font-medium uppercase tracking-wide text-primary-600">
                        {levelLabel(item.jobLevel)} · {item.kindName}
                      </div>
                      <div className="font-medium text-secondary-900">{item.title}</div>
                    </div>
                  </div>
                  {canWrite ? (
                    <div className="shrink-0">
                      <EditIconButton onClick={() => startEdit(item)} />
                      <DeleteIconButton onClick={() => void handleDelete(item)} />
                    </div>
                  ) : null}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
}
